import React, {
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'

import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

import { LOADING_MESSAGE_TIMEOUT, LONGER_THAN_EXPECTED_TIMEOUT } from '@/modules/trials/constants'
import { ContinueCreationApiResponse, GetStoreApiResponse } from '@/modules/trials/interfaces'
import { PollingService } from '@/modules/trials/services/polling-service/service'
import { TrialContextType, TrialField } from '@/types/trials'
import * as Sentry from '@sentry/nextjs'

import { sendDataLayerEvent } from '@/utils/analytics/events'
import { useCsrf } from '@/utils/csrf/useCsrf'
import { trialLayout } from '@/utils/data/trials/form'
import { handleDriftFastlane, sendDriftEvent } from '@/utils/drift/events'
import { useLoadRecaptcha } from '@/utils/recaptcha/AvailablePathnames/useLoadRecaptcha'
import { AuthCodeExchangeResponse, authCodeExchange } from '@/utils/saml/authCodeExchange'
import { AuthenticationService } from '@/utils/saml/services/authenticationService'
import { continueCreation } from '@/utils/trials/continueCreation'

import { useAnalyticsContext } from '@/components/Scripts/Segment/AnalyticsProvider'

const initialValues = {} as TrialContextType

const TrialContext = createContext(initialValues)

export function useTrial() {
	const context = useContext(TrialContext)

	if (context === undefined) {
		throw new Error('useTrial must be used within a TrialProvider')
	}

	return context
}

export function TrialProvider({
	children,
	driftPlaybookId,
	skuValue,
	loadingSecondaryText
}: {
	children: ReactNode
	driftPlaybookId?: string
	skuValue: string
	loadingSecondaryText?: string
}) {
	const { t } = useTranslation('trials')
	const router = useRouter()
	const authCodeParam = router?.query?.auth_code
	const { analytics } = useAnalyticsContext()

	// Get a CSRF token only if the form is a trial flow
	const csrfToken = useCsrf(true)

	const { additionalFields, fields } = trialLayout

	const [currentStep, setCurrentStep] = useState<number>(1)
	const [formFields, setFormFields] = useState<TrialField[]>(fields)
	const [storeInfo, setStoreInfo] = useState({ storeId: '', userEmail: '' })

	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [loadingMessage, setLoadingMessage] = useState<string>('')

	const [showWebsiteField, setShowWebsiteField] = useState(false)
	const [storeLoginUrl, setStoreLoginUrl] = useState('')
	const [showRedirectMessage, setShowRedirectMessage] = useState(false)
	const [showCuiMessage, setShowCuiMessage] = useState(false)
	const [showError, setShowError] = useState(false)
	const [errorMsg, setErrorMsg] = useState('')
	const [talon, setTalon] = useState('{"version": 6, "status": -1}')
	const [recaptchaId, setRecaptchaId] = useState(-1)
	const [recaptchaError, setRecaptchaError] = useState(false)
	const [email, setEmail] = useState('')
	const [values, setValues] = useState<any>(null)
	const [disableButton, setDisableButton] = useState(false)
	const [isAccountAvailable, setIsAccountAvailable] = useState(false)

	const authenticationService = useMemo(() => new AuthenticationService(), [])
	const pollingService = useMemo(() => new PollingService(), [])
	const recaptchaRef = useRef<HTMLDivElement>(null)
	const recaptchaLoaded = useLoadRecaptcha()
	// Send the event to Segment and DataLayer when the currentStep changes
	useEffect(() => {
		if (window?.navigator?.userAgent === process.env.NEXT_PUBLIC_TRIALS_FORM_USER_AGENT) {
			return
		}
		analytics?.track('trial form', { step: currentStep.toString() })

		sendDataLayerEvent('trial form', {
			step: currentStep
		})
	}, [analytics, currentStep])

	useEffect(() => {
		if (errorMsg) {
			// Show the error when there is an error message
			setShowError(true)
		}
	}, [errorMsg])

	// If there is a drift playbook id, send the event to Drift
	useEffect(() => {
		if (driftPlaybookId && isAccountAvailable && isLoading && authCodeParam === undefined) {
			handleDriftFastlane(values, Number(driftPlaybookId))
		}
	}, [driftPlaybookId, values, authCodeParam, isAccountAvailable, isLoading])

	const packageName = useMemo(() => skuValue.replace(/\(sku:.*\)/, '').trim(), [skuValue])

	const sendTransactionEvent = useCallback(
		(data: GetStoreApiResponse) => {
			const transaction = {
				id: data.storeUuid,
				flow: 'Free Trial',
				products: [
					{
						id: skuValue,
						name: packageName,
						description: ''
					}
				],
				store: {
					id: data.storeId,
					domain: data.canonicalHostname,
					fullDomain: data.primaryHostname
				},
				user: {
					country: data.userCountry,
					ecommerce_experience: null,
					industry: null
				}
			}

			if (window?.navigator?.userAgent === process.env.NEXT_PUBLIC_TRIALS_FORM_USER_AGENT) {
				return
			}

			sendDataLayerEvent('transaction new flow', { transaction })
		},
		[packageName, skuValue]
	)

	// If there is an auth code in the URL, exchange it for a JWT
	useEffect(() => {
		const authCode = Array.isArray(authCodeParam)
			? authCodeParam[authCodeParam.length - 1]
			: authCodeParam

		if (!authCode) {
			return
		}

		setLoadingMessage('')
		setIsLoading(true)

		setTimeout(() => setLoadingMessage(loadingSecondaryText ?? ''), LOADING_MESSAGE_TIMEOUT)
		setTimeout(
			() => setLoadingMessage(t('trialForm.loading.longerThanExpected')),
			LONGER_THAN_EXPECTED_TIMEOUT
		)

		authCodeExchange({ authCode })
			.then((authCodeExchangeResponse: AuthCodeExchangeResponse) => {
				if (!authCodeExchangeResponse.jwt) {
					return
				}

				continueCreation(authCodeExchangeResponse)
					.then((response: Response) => {
						return response.json().then((data: ContinueCreationApiResponse) => {
							authenticationService.setSessionCookie({
								email: data.emailAddress,
								newAccount: false,
								storeUuid: data.storeUuid
							})

							pollingService
								.pollStoreApi()
								.then((data: GetStoreApiResponse) => {
									setStoreInfo({ storeId: data.storeId, userEmail: data.emailAddress })
									setStoreLoginUrl(data.storeUrl)

									sendTransactionEvent(data)

									sendDriftEvent('Trial Form Submit', { path: window.location.pathname })

									// Show optional questions
									setCurrentStep(2)
									setIsLoading(false)
								})
								.catch((e: Error) => {
									if (e instanceof Error) {
										throw e
									}

									throw new Error('Error polling store')
								})
						})
					})
					.catch((err: Error) => {
						switch (parseInt(err.message)) {
							case 409:
								// Show C.UI modal
								setShowCuiMessage(true)
								setErrorMsg('')
								setLoadingMessage('')
								break
							case 524:
								setErrorMsg(t('trialForm.loading.longerThanExpected'))
								setLoadingMessage('')
								break
							default:
								setErrorMsg(t('trialForm.status.errors.default'))
								setLoadingMessage('')
								Sentry.captureException(
									new Error('Error with continuing store creation', { cause: err.message })
								)
						}

						setIsLoading(false)
					})
			})
			.catch((err: Error) => {
				setErrorMsg(t('trialForm.status.errors.default'))
				setIsLoading(false)

				Sentry.captureException(new Error('Error exchanging auth code', { cause: err.message }))
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authCodeParam, authenticationService])

	// Set new fields when currentStep changes
	useEffect(() => {
		if (currentStep === 2) {
			setFormFields(additionalFields)
		} else {
			setFormFields(fields)
		}
	}, [additionalFields, currentStep, fields])

	// Add the url field to the additional fields for step 2
	useEffect(() => {
		if (currentStep === 2) {
			if (!showWebsiteField) {
				const removeFromAdditionalFields = additionalFields.filter(
					(field: TrialField) => field.fieldName !== 'currentWebsiteUrl'
				)
				setFormFields(removeFromAdditionalFields)

				return
			}

			const urlField: TrialField[] = [
				{
					fieldName: 'currentWebsiteUrl',
					fieldType: 'text',
					id: 'currentWebsiteUrl',
					label: 'What is the URL?',
					required: true
				}
			]

			const newAdditionalFields = additionalFields.map((field: TrialField) => ({ ...field }))

			newAdditionalFields.splice(1, 0, ...urlField)

			setFormFields([...newAdditionalFields])
		}
	}, [additionalFields, showWebsiteField, currentStep])

	// reCAPTCHA
	const loadRecaptcha = () => {
		if (
			!window?.navigator ||
			window?.navigator?.userAgent === process.env.NEXT_PUBLIC_TRIALS_FORM_USER_AGENT ||
			!recaptchaLoaded
		) {
			return
		}

		grecaptcha?.enterprise?.ready(() => {
			if (!recaptchaRef.current) {
				return
			}
			try {
				const id = grecaptcha.enterprise.render(recaptchaRef.current, {
					sitekey: process.env.NEXT_PUBLIC_RECAPTCHA_CHECKBOX_SITE_KEY || '',
					// @ts-ignore: We know action is not part of the type definition
					action: 'trial_submit'
				})

				setRecaptchaId(id)
			} catch (e) {
				console.error(e)
			}
		})
	}

	useEffect(() => {
		if (recaptchaId !== -1) {
			return
		}

		loadRecaptcha()
	}, [recaptchaId, recaptchaLoaded])

	const resetRecaptcha = () => {
		setRecaptchaId(-1)
		if (typeof grecaptcha !== 'undefined' && recaptchaId !== -1) {
			grecaptcha.enterprise.reset(recaptchaId)
		}
	}

	return (
		<TrialContext.Provider
			value={{
				skuValue,
				csrfToken,
				currentStep,
				setCurrentStep,
				formFields,
				setFormFields,
				storeInfo,
				setStoreInfo,
				isLoading,
				setIsLoading,
				loadingMessage,
				setLoadingMessage,
				showWebsiteField,
				setShowWebsiteField,
				storeLoginUrl,
				setStoreLoginUrl,
				showRedirectMessage,
				setShowRedirectMessage,
				showCuiMessage,
				setShowCuiMessage,
				showError,
				setShowError,
				errorMsg,
				setErrorMsg,
				talon,
				setTalon,
				recaptchaId,
				setRecaptchaId,
				recaptchaError,
				setRecaptchaError,
				email,
				setEmail,
				values,
				setValues,
				disableButton,
				setDisableButton,
				authenticationService,
				pollingService,
				recaptchaRef,
				loadRecaptcha,
				resetRecaptcha,
				sendTransactionEvent,
				loadingSecondaryText,
				isAccountAvailable,
				setIsAccountAvailable
			}}
		>
			{children}
		</TrialContext.Provider>
	)
}
